import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Notification from "../../component/notification";
import { Modal, Button } from "react-bootstrap";
import Loader from "../../component/loader";
import { db } from "../../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { getDocs, collection, getDoc } from "firebase/firestore";
import { FiSettings } from "react-icons/fi";

const ITEMS_PER_PAGE = 10;

const CourseContent = () => {
  const location = useLocation();
  //console.log("Received state:", location.state);
  const navigate = useNavigate();
  const course = location.state?.course;
  const duration = course?.duration || 0;
  const courseTitle = course?.title || "Course";
  const [loading, setLoading] = useState(false);

  // State to store actual document IDs and their mapping
  const [dayDocumentIds, setDayDocumentIds] = useState({}); // { 1: "day 1", 2: "day 2", ... }
  const [availableDays, setAvailableDays] = useState([]);

  // Create daily content based on available days
  const [dailyContent, setDailyContent] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedContent, setPaginatedContent] = useState([]);

  // Notification state
  const [notification, setNotification] = useState({
    message: "",
    type: "",
    visible: false,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedDay] = useState(null);
  const [contentMap, setContentMap] = useState({});
  const [dayTitles, setDayTitles] = useState({});

  // Function to extract numerical day from document ID
  const extractDayNumber = (docId) => {
    // Handle formats like "day 1", "day1", "Day 1", etc.
    const match = docId.match(/day\s*(\d+)/i);
    return match ? parseInt(match[1]) : null;
  };

  // Function to get document ID from day number
  const getDocumentId = (dayNumber) => {
    return dayDocumentIds[dayNumber] || `day ${dayNumber}`;
  };

  // Fetch existing day documents and create mapping
  const fetchExistingDayDocuments = async () => {
    if (!course?.id) return;

    setLoading(true);
    try {
      const dayCollectionRef = collection(db, 'courses', course.id, 'day');
      const snapshot = await getDocs(dayCollectionRef);
      
      const docIds = {};
      const days = [];
      
      snapshot.forEach((doc) => {
        const docId = doc.id;
        const dayNumber = extractDayNumber(docId);
        
        if (dayNumber) {
          docIds[dayNumber] = docId;
          days.push(dayNumber);
        }
      });

      // If no existing documents, create mapping for expected duration
      if (days.length === 0) {
        for (let i = 1; i <= duration; i++) {
          docIds[i] = `day ${i}`;
          days.push(i);
        }
      } else {
        // Ensure we have all days up to duration
        for (let i = 1; i <= duration; i++) {
          if (!docIds[i]) {
            docIds[i] = `day ${i}`;
            days.push(i);
          }
        }
      }

      days.sort((a, b) => a - b);
      
      setDayDocumentIds(docIds);
      setAvailableDays(days);
      
      // Create daily content array
      const content = days.map(dayNum => ({
        day: dayNum,
        dayId: dayNum,
        title: `Content for Day ${dayNum}`,
        description: "",
      }));
      
      setDailyContent(content);
      
      // Update pagination
      const newTotalPages = Math.ceil(content.length / ITEMS_PER_PAGE);
      setTotalPages(newTotalPages);
      
      //console.log("Day document mapping:", docIds);
      //console.log("Available days:", days);
      
    } catch (error) {
      console.error("Error fetching day documents:", error);
      setNotification({
        message: "Error fetching day documents",
        type: "danger",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Update paginated content when dailyContent or currentPage changes
  React.useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setPaginatedContent(dailyContent.slice(startIndex, endIndex));
  }, [dailyContent, currentPage]);

  // Fetch day titles from existing documents
  const fetchDayTitles = async () => {
    if (!course?.id || availableDays.length === 0) return;

    setLoading(true);
    try {
      const titles = {};

      for (const dayNumber of availableDays) {
        const docId = getDocumentId(dayNumber);
        const dayDoc = await getDoc(doc(db, 'courses', course.id, 'day', docId));
        
        if (dayDoc.exists()) {
          titles[dayNumber] = dayDoc.data().dayTitle || `Day ${dayNumber}`;
        } else {
          titles[dayNumber] = `Day ${dayNumber}`;
        }
      }

      setDayTitles(titles);
    } catch (error) {
      console.error("Error fetching day titles:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch assignments data
  const fetchAssignments = async () => {
    if (!course?.id || availableDays.length === 0) return;

    setLoading(true);
    try {
      const map = {};

      for (const dayNumber of availableDays) {
        const docId = getDocumentId(dayNumber);
        const assignmentsRef = collection(db, 
          `courses/${course.id}/day/${docId}/assignments`
        );
        const snapshot = await getDocs(assignmentsRef);
        map[dayNumber] = snapshot.docs.map(doc => doc.data());
      }

      setContentMap(map);
    } catch (error) {
      console.error("Error fetching assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial effect to fetch existing documents
  React.useEffect(() => {
    const fetchExistingDayDocuments = async () => {
      if (!course?.id || duration <= 0) return;

      setLoading(true);
      try {
        const dayCollectionRef = collection(db, 'courses', course.id, 'day');
        const snapshot = await getDocs(dayCollectionRef);
        const docIds = {};
        const days = [];

        snapshot.forEach((doc) => {
          const docId = doc.id;
          const dayNumber = extractDayNumber(docId);

          if (dayNumber) {
            docIds[dayNumber] = docId;
            days.push(dayNumber);
          }
        });

        // If no existing documents, create mapping for expected duration
        if (days.length === 0) {
          for (let i = 1; i <= duration; i++) {
            docIds[i] = `day ${i}`;
            days.push(i);
          }
        } else {
          // Ensure we have all days up to duration
          for (let i = 1; i <= duration; i++) {
            if (!docIds[i]) {
              docIds[i] = `day ${i}`;
              days.push(i);
            }
          }
        }

        days.sort((a, b) => a - b);

        setDayDocumentIds(docIds);
        setAvailableDays(days);

        // Create daily content array
        const content = days.map(dayNum => ({
          day: dayNum,
          dayId: dayNum,
          title: `Content for Day ${dayNum}`,
          description: "",
        }));

        setDailyContent(content);

        // Update pagination
        const newTotalPages = Math.ceil(content.length / ITEMS_PER_PAGE);
        setTotalPages(newTotalPages);

        //console.log("Day document mapping:", docIds);
        //console.log("Available days:", days);

      } catch (error) {
        console.error("Error fetching day documents:", error);
        setNotification({
          message: "Error fetching day documents",
          type: "danger",
          visible: true,
        });
      } finally {
        setLoading(false);
      }
    };

    if (course?.id && duration > 0) {
      fetchExistingDayDocuments();
    }
  }, [course?.id, duration]);

  // Effect to fetch titles and assignments after documents are loaded
  React.useEffect(() => {
    if (availableDays.length > 0) {
      fetchDayTitles();
      setTimeout(() => {
        fetchAssignments();
      }, 500);
    }
  }, [availableDays]);

  const handleDelete = async () => {
    if (!course?.id || !selectedDay) return;
    
    try {
      const docId = getDocumentId(selectedDay);
      const dayDocRef = doc(db, "courses", course.id, "day", docId);
      
      await updateDoc(dayDocRef, {
        videoLink: "",
        documentLink: "",
        quiz: [],
        codingQuestion: "",
        questionAnswer: "",
      });
      
      setNotification({
        message: `Content for Day ${selectedDay} deleted successfully.`,
        type: "success",
        visible: true,
      });
      setShowModal(false);

      // Refresh assignments data
      fetchAssignments();
    } catch (error) {
      console.error("Error deleting content:", error);
      setNotification({
        message: `Failed to delete content for Day ${selectedDay}.`,
        type: "danger",
        visible: true,
      });
      setShowModal(false);
    }
  };

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const handleAddContent = (dayNumber) => {
    const docId = getDocumentId(dayNumber);
    navigate("/content", { 
      state: { 
        course, 
        day: dayNumber,
        documentId: docId // Pass the actual document ID
      } 
    });
  };

  // Auto-hide notifications
  React.useEffect(() => {
    if (notification.visible) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, visible: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.visible]);

  return (
    <div className="container-fluid py-4" style={{ position: "relative" }}>
      {loading && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(255,255,255,0.7)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Loader />
        </div>
      )}
      <div className="bg-white rounded-3 p-4 shadow-sm border">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold mb-0">{courseTitle} - Daily Content</h4>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
            aria-label="Back to Courses"
          >
            <i className="bi bi-arrow-left-square-fill"></i> Back to Courses
          </button>
        </div>
        <div className="text-muted mb-3">
          Manage daily content for <b>{courseTitle}</b>
          {Object.keys(dayDocumentIds).length > 0 && (
            <small className="d-block mt-1 text-info">
              Found {Object.keys(dayDocumentIds).length} day documents in database
            </small>
          )}
        </div>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: "10%" }}>Day</th>
                <th style={{ width: "35%" }}>Content Title</th>
                <th style={{ width: "35%" }}>No. of Responses</th>
                <th style={{ width: "10%", textAlign: "center" }}></th>
                <th style={{ width: "10%", textAlign: "center" }}></th>
              </tr>
            </thead>
            <tbody>
              {paginatedContent.map((content) => (
                <tr key={content.day}>
                  <td>
                    {content.day}
                    <small className="d-block text-muted" style={{ fontSize: '0.75em' }}>
                     
                    </small>
                  </td>
                  <td>{dayTitles[content.dayId] || content.title}</td>
                  <td>
                    <span
                      style={{
                        color: "#2563eb",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        padding: "2px 4px",
                        borderRadius: "4px",
                        backgroundColor: "#dbeafe",
                      }}
                      onClick={() =>
                        navigate("/responce", {
                          state: { 
                            course, 
                            dayId: content.dayId,
                            documentId: getDocumentId(content.dayId)
                          },
                        })
                      }
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#bfdbfe";
                        e.target.style.transform = "scale(1.02)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#dbeafe";
                        e.target.style.transform = "scale(1)";
                      }}
                    >
                      {Array.isArray(contentMap[content.dayId])
                        ? contentMap[content.dayId].length
                        : 0}{" "}
                      assignments
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="btn btn-outline-primary btn-sm me-2"
                      onClick={() => handleAddContent(content.day)}
                    >
                      Add Content
                    </button>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="btn btn-link p-1"
                      title="Settings"
                      onClick={() =>
                        navigate("/daysetting", {
                          state: { 
                            courseId: course.id, 
                            dayId: content.dayId,
                            documentId: getDocumentId(content.dayId)
                          },
                        })
                      }
                    >
                      <FiSettings />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handlePrev}
            disabled={currentPage === 1}
            aria-label="Previous Page"
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleNext}
            disabled={currentPage === totalPages}
            aria-label="Next Page"
          >
            Next
          </button>
        </div>

        {/* Notification Component */}
        <Notification
          message={notification.message}
          type={notification.type}
          visible={notification.visible}
          onClose={() => setNotification({ ...notification, visible: false })}
          index={1}
        />

        {/* Delete Confirmation Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete content for Day {selectedDay}?
            <br />
            <small className="text-muted">Document ID: {getDocumentId(selectedDay)}</small>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CourseContent;